import React, { useState, useRef, useEffect } from "react";
import {
  Card,
  CardHeader,
  CardContent,
  Typography,
  Box,
  IconButton,
  Tooltip,
  Radio,
  RadioGroup,
  FormControlLabel,
} from "@mui/material";
import {
  AddCircle as AddCircleIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Print as PrintIcon,
} from "@mui/icons-material";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useSnackbar } from "notistack";

import TableComponent from "../../../../TableComponent/TableComponent";
import VaccinationModal from "./VaccinationModal";
import {
  getVaccinationRecords,
  getAllVaccinationRecords,
  deleteVaccinationRecord,
} from "../../../../../api/services/ReferenceData/vaccination";
import { columns, VaccinationPrintColumns } from "./TableInfo/TableHeader";
import PrintComponent from "../../../../Print";
import { QK } from "../../../../../base/qk";
import ConfirmModal from "../../../../ConfirmModal/ConfirmModal";

const PRIMARY_BLUE = "#004D99";
const LIGHT_BLUE_BG = "#E3F2FD";

const VaccinationRecord = () => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [showVaccinationModal, setShowVaccinationModal] = useState(false);
  const [userType, setUserType] = useState("client");
  const [allData, setAllData] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(null); // for edit
  const [showConfirm, setShowConfirm] = useState(false); // for delete modal
  const [recordToDelete, setRecordToDelete] = useState(null); // store record to delete
  const [isDeleting, setIsDeleting] = useState(false);
  const printRef = useRef();

  const handleVaccinationShow = (record = null) => {
    setSelectedRecord(record);
    setShowVaccinationModal(true);
  };
  const handleVaccinationClose = () => {
    setShowVaccinationModal(false);
    setSelectedRecord(null);
  };

  const { data: vaccinationRecords, isLoading, refetch } = useQuery(
    [QK.vaccinationRecords, page],
    () => getVaccinationRecords(page),
    {
      onError: () => enqueueSnackbar("Failed to load vaccination records", { variant: "error" }),
      keepPreviousData: true,
    }
  );

  const handlePageChange = (newPage) => setPage(newPage);

  // Delete mutation
  const deleteMutation = useMutation(
    (id) => deleteVaccinationRecord(id),
    {
      onSuccess: () => {
        enqueueSnackbar("Record deleted successfully!", { variant: "success" });
        queryClient.invalidateQueries(QK.vaccinationRecords);
        setShowConfirm(false);
        setRecordToDelete(null);
        setIsDeleting(false);
      },
      onError: () => {
        enqueueSnackbar("Failed to delete record", { variant: "error" });
        setIsDeleting(false);
      },
    }
  );

  const handleDeleteClick = (record) => {
    setRecordToDelete(record);
    setShowConfirm(true);
  };

  const handleConfirmDelete = () => {
    if (!recordToDelete) return;
    setIsDeleting(true);
    deleteMutation.mutate(recordToDelete.id);
  };

  const actions = [
    {
      icon: <EditIcon />,
      tooltip: "Edit",
      color: "primary",
      handler: (row) => handleVaccinationShow(row),
    },
    {
      icon: <DeleteIcon />,
      tooltip: "Delete",
      color: "error",
      handler: handleDeleteClick,
    },
  ];

  const tableData = (vaccinationRecords?.results || [])
    .filter((record) => (userType === "client" ? record.client : record.candidate))
    .map((record) => ({
      ...record,
      type: record.client ? "Service User" : record.candidate ? "Employee" : "-",
      given: record.is_given ? "Yes" : "No",
    }));

  useEffect(() => {
    if (allData.length > 0 && printRef.current) {
      printRef.current.handlePrint();
    }
  }, [allData]);

  const handlePrintClick = async () => {
    try {
      const records = await getAllVaccinationRecords(userType);
      const data = (records?.results || []).map((record) => ({
        ...record,
        type: record.client ? "Service User" : record.candidate ? "Employee" : "-",
        given: record.is_given ? "Yes" : "No",
      }));
      setAllData(data);
    } catch (error) {
      enqueueSnackbar("Failed to fetch records for printing", { variant: "error" });
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Card sx={{ mb: 3 }}>
        <CardHeader
          title={
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="h5" sx={{ color: PRIMARY_BLUE, fontWeight: "bold" }}>
                Vaccination Record
              </Typography>
              <Box display="flex" gap={1}>
                <Tooltip title="Add">
                  <IconButton color="primary" onClick={() => handleVaccinationShow()}>
                    <AddCircleIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Print">
                  <IconButton color="primary" onClick={handlePrintClick}>
                    <PrintIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          }
          sx={{ backgroundColor: LIGHT_BLUE_BG }}
        />

        <VaccinationModal
          show={showVaccinationModal}
          onHide={handleVaccinationClose}
          userType={userType}
          onSuccess={refetch}
          record={selectedRecord}
        />

        <ConfirmModal
          show={showConfirm}
          handleClose={() => setShowConfirm(false)}
          handleConfirm={handleConfirmDelete}
          title="Delete Vaccination Record"
          body="Are you sure you want to delete this vaccination record? This action cannot be undone."
          isLoading={isDeleting}
          isDelete
        />

        <CardContent>
          <RadioGroup
            row
            value={userType}
            onChange={(e) => setUserType(e.target.value)}
            sx={{ mb: 3 }}
          >
            <FormControlLabel value="client" control={<Radio />} label="Service User" />
            <FormControlLabel value="candidate" control={<Radio />} label="Employee" />
          </RadioGroup>

          <TableComponent
            columns={columns}
            data={tableData}
            actions={actions}
            isLoading={isLoading}
            pagination={{
              totalPages: Math.ceil((vaccinationRecords?.count || 1) / 10),
              currentPage: page,
            }}
            onPageChange={handlePageChange}
          />

          <PrintComponent
            ref={printRef}
            title="Vaccination Records"
            data={allData}
            columns={VaccinationPrintColumns}
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default VaccinationRecord;